import React, { useState, useEffect } from 'react';
import { getLatestDate, getMinDate, getPriceForDate } from '../services/metalPriceService';
import { calculateNisab, calculateZakat, getNisabOptions, getNisabValue } from '../utils/calculations';
import { ZakatAsset, ZakatResult } from '../types';

const ZakatCalculator: React.FC = () => {
  const [zakatDate, setZakatDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [minDate] = useState(getMinDate());
  const [nisabOption, setNisabOption] = useState('gold-sh-samer');
  const [asset, setAsset] = useState<ZakatAsset>({
    goldWeight: 0,
    silverWeight: 0,
    cashValue: 0,
    stockValue: 0,
    deductibleDebt: 0,
  });
  const [result, setResult] = useState<ZakatResult | null>(null);
  const [nisabValue, setNisabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const latest = await getLatestDate();
      setMaxDate(latest);
      setZakatDate(latest);
    };
    init();
  }, []);

  const handleCalculate = async () => {
    if (!zakatDate) return;
    setLoading(true);
    
    const prices = await getPriceForDate(zakatDate);
    if (prices) {
      const nisabCalc = calculateNisab(prices.gold, prices.silver);
      const selectedNisab = getNisabValue(nisabOption, nisabCalc);
      setNisabValue(selectedNisab);
      
      const zakatResult = calculateZakat(selectedNisab, prices.gold, prices.silver, asset);
      setResult(zakatResult);
    }
    
    setLoading(false);
  };

  return (
    <div className="container mt-5">
      <h2>Zakat Calculator</h2>
      
      <div className="card mt-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Zakat Date</label>
              <input
                type="date"
                className="form-control"
                value={zakatDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => setZakatDate(e.target.value)}
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Nisab Option</label>
              <select
                className="form-select"
                value={nisabOption}
                onChange={(e) => setNisabOption(e.target.value)}
              >
                {getNisabOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Gold Weight (grams)</label>
              <input
                type="number"
                className="form-control"
                value={asset.goldWeight}
                onChange={(e) => setAsset({ ...asset, goldWeight: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Silver Weight (grams)</label>
              <input
                type="number"
                className="form-control"
                value={asset.silverWeight}
                onChange={(e) => setAsset({ ...asset, silverWeight: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Cash Value (£)</label>
              <input
                type="number"
                className="form-control"
                value={asset.cashValue}
                onChange={(e) => setAsset({ ...asset, cashValue: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Stock Value (£)</label>
              <input
                type="number"
                className="form-control"
                value={asset.stockValue}
                onChange={(e) => setAsset({ ...asset, stockValue: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Deductible Debt (£)</label>
              <input
                type="number"
                className="form-control"
                value={asset.deductibleDebt}
                onChange={(e) => setAsset({ ...asset, deductibleDebt: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <button className="btn btn-primary" onClick={handleCalculate} disabled={loading}>
            {loading ? 'Calculating...' : 'Calculate Zakat'}
          </button>
        </div>
      </div>

      {result && (
        <div className="card mt-4">
          <div className="card-header bg-success text-white">
            <h4>Zakat Calculation Result</h4>
          </div>
          <div className="card-body">
            <table className="table">
              <tbody>
                <tr>
                  <td><strong>Nisab Threshold:</strong></td>
                  <td>£{nisabValue.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Gold Cash Value:</strong></td>
                  <td>£{result.goldCashValue.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Silver Cash Value:</strong></td>
                  <td>£{result.silverCashValue.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Total Asset Value:</strong></td>
                  <td>£{result.totalAssetValue.toFixed(2)}</td>
                </tr>
                <tr className="table-success">
                  <td><strong>Zakat Owed (2.5%):</strong></td>
                  <td><strong>£{result.zakatOwed.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
            
            {result.zakatOwed === 0 && (
              <div className="alert alert-info">
                Your total assets are below the Nisab threshold. No Zakat is owed.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZakatCalculator;
